import React, { useEffect, useState } from 'react';
import { AppBar, Toolbar, Stack, Button, Menu, MenuItem, Box, Link as MuiLink, Container, IconButton, Tooltip, Typography, Drawer, List, ListItem, ListItemButton, ListItemText, Divider, useMediaQuery, useTheme } from '@mui/material';
import ProfileMenu from './ProfileMenu';
import NotificationBell from './NotificationBell';
import MentorSearch from './MentorSearch';
import { useAuth } from '../context/AuthContext';
import { useStudyPlans } from '../context/StudyPlansContext';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import MenuIcon from '@mui/icons-material/Menu';

function LogoMark() {
  return (
    <RouterLink to="/" style={{ display: 'inline-block' }} aria-label="Home">
      <Box
        component="img"
        src={`${process.env.PUBLIC_URL}/logo.png`}
        alt="Arithmetic Infotech"
        sx={{
          width: 140,
          height: 60,
          objectFit: 'contain'
        }}
      />
    </RouterLink>
  );
}

export default function TopNav() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { isAuthenticated, logout: authLogout, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [paperAnchor, setPaperAnchor] = React.useState(null);
  const [mentorOpen, setMentorOpen] = useState(false);
  const [gettingStartedAnchor, setGettingStartedAnchor] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { tracks, loadTracks, loading: tracksLoading } = useStudyPlans();
  React.useEffect(() => {
    if (user?.id && !tracks?.length) {
      loadTracks(user.id).catch(() => { });
    }
  }, [user?.id]);

  const handleLogout = async () => {
    try {
      await authLogout();
    } catch (e) {
    }
    navigate('/');
  };

  const handleTrackProgress = () => {
    navigate('/track-progress');
  };

  const openGettingStarted = (e) => setGettingStartedAnchor(e.currentTarget);
  const closeGettingStarted = () => setGettingStartedAnchor(null);
  const openStudyMenu = (e) => {
    setAnchorEl(e.currentTarget);
    if (!tracks?.length && user?.id) {
      loadTracks(user.id).catch(() => { });
    }
  };
  const closeStudyMenu = () => setAnchorEl(null);

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const navLinks = [
    { label: 'Explore Exams', path: '/learn' },
    { label: 'AI Smart Papers', path: '/genai-papers' },
    { label: 'Subject Practice', path: '/subject-practice' },
  ];

  const adminLinks = [
    { label: 'Import Papers', path: '/admin/import' },
    { label: 'Import Config', path: '/admin/import-config' },
    { label: 'Support Tickets', path: '/admin/support-tickets' },
    { label: 'Ad Reports', path: '/admin/ads-summary' },
    { label: 'P0 Launch Health', path: '/admin/p0' },
  ];

  const drawerList = () => (
    <Box
      sx={{ width: 280 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
        <LogoMark />
      </Box>
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/study-paths/select')}>
            <ListItemText primary="Create Study Plan" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/study-paths/mine')}>
            <ListItemText primary="Your Study Plans" />
          </ListItemButton>
        </ListItem>
        <Divider sx={{ my: 1 }} />
        {navLinks.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton onClick={() => navigate(item.path)}>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      {user?.role === 'admin' && (
        <>
          <Divider />
          <List>
            <ListItem>
              <Typography variant="overline" color="text.secondary">Admin Area</Typography>
            </ListItem>
            {adminLinks.map((item) => (
              <ListItem key={item.label} disablePadding>
                <ListItemButton onClick={() => navigate(item.path)}>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(8px)',
        zIndex: (theme) => theme.zIndex.drawer + 1
      }}
    >
      <Container maxWidth={false}>
        <Toolbar disableGutters sx={{ justifyContent: 'space-between', height: 64, minHeight: '64px !important' }}>
          
          {/* Left-side nav */}
          <Stack direction="row" spacing={1} alignItems="center">
            {isAuthenticated && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={toggleDrawer(true)}
                sx={{ color: 'text.primary', ml: { xs: 0, sm: -1 } }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <LogoMark />
          </Stack>

          {/* Right-side nav */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
            {isAuthenticated ? (
              <>
                <Tooltip title="Search & Ask Mentor">
                  <IconButton onClick={() => setMentorOpen(true)} color="primary" size="small" sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', borderRadius: 2 }}>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <NotificationBell />

                {!isMobile && (
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={openGettingStarted}
                    startIcon={<RocketLaunchIcon fontSize="small" />}
                    endIcon={<ArrowDropDownIcon />}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: '0.8rem', px: 1.5, mx: 1, borderRadius: 2, boxShadow: 'none' }}
                  >
                    Get Started
                  </Button>
                )}
                
                <Menu
                  anchorEl={gettingStartedAnchor}
                  open={Boolean(gettingStartedAnchor)}
                  onClose={closeGettingStarted}
                  PaperProps={{
                    sx: {
                      mt: 1.5,
                      borderRadius: '16px',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                      border: '1px solid rgba(226, 232, 240, 0.8)',
                      minWidth: 220
                    }
                  }}
                >
                  <MenuItem onClick={() => { closeGettingStarted(); navigate('/study-paths/select'); }}>
                    <Typography variant="body2" fontWeight={600} color="primary.main">1. Set Your Goals</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => { closeGettingStarted(); navigate('/learn'); }}>
                    <Typography variant="body2" fontWeight={600} color="text.primary">2. Explore Exams</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => { closeGettingStarted(); navigate('/subject-practice'); }}>
                    <Typography variant="body2" fontWeight={600} color="text.primary">3. Practice Topics</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => { closeGettingStarted(); navigate('/genai-papers'); }}>
                    <Typography variant="body2" fontWeight={600} color="text.primary">4. Take AI Mocks</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => { closeGettingStarted(); setMentorOpen(true); }}>
                    <Typography variant="body2" fontWeight={600} color="text.primary">5. Ask the Mentor</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => { closeGettingStarted(); navigate('/track-progress'); }}>
                    <Typography variant="body2" fontWeight={600} color="text.primary">6. Track Progress</Typography>
                  </MenuItem>
                </Menu>

                <Box sx={{ ml: 1 }}>
                  <ProfileMenu onLogout={handleLogout} onTrackProgress={handleTrackProgress} user={user} />
                </Box>
              </>
            ) : (
              <Button
                variant="contained"
                component={RouterLink}
                to="/login"
                sx={{
                  borderRadius: '16px',
                  px: 4,
                  fontWeight: 700
                }}
              >
                Login
              </Button>
            )}
          </Stack>
        </Toolbar>
      </Container>
      
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
          }
        }}
      >
        {drawerList()}
      </Drawer>
      </AppBar>

      {mentorOpen && <MentorSearch onClose={() => setMentorOpen(false)} />}
    </>
  );
}
